<template>
  <div :class="['terminal', theme]">
    <button class="toggle-btn" @click="toggleTheme">
      {{ theme === 'dark' ? '🌙 Sombre' : '☀️ Clair' }}
    </button>
    <div class="history">
      <div v-for="(entry, i) in history" :key="i">
        <div class="command-line">
          <span class="prompt">Alexandre@dev:~$</span> <span class="cmd">{{ entry.command }}</span>
        </div>
        <div class="response">{{ entry.response }}</div>
      </div>
    </div>
    <form class="input-line" @submit.prevent="handleCommand">
      <span class="prompt">Alexandre@dev:~$</span>
      <input
        v-model="currentCommand"
        @keydown.up.prevent="previousCommand"
        @keydown.down.prevent="nextCommand"
        class="input"
        autocomplete="off"
        autofocus
      />
    </form>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import axios from 'axios'

const history = ref([])
const currentCommand = ref('')
const lang = ref('fr')
const commandHistory = ref([])
const historyIndex = ref(-1)
const theme = ref('dark')

const setBodyTheme = (themeValue) => {
  document.body.style.background = themeValue === 'dark' ? '#1a1a1a' : '#f5f5f5';
  document.body.style.color = themeValue === 'dark' ? '#eee' : '#222';
}

onMounted(() => {
  setBodyTheme(theme.value)
})

watch(theme, (newTheme) => {
  setBodyTheme(newTheme)
})

const toggleTheme = () => {
  theme.value = theme.value === 'dark' ? 'light' : 'dark'
}

const handleCommand = async () => {
  const command = currentCommand.value.trim()
  if (!command) return

  // Gestion du changement de langue
  if (command.startsWith('lang ')) {
    const newLang = command.split(' ')[1]
    if (['fr', 'en'].includes(newLang)) {
      lang.value = newLang
      history.value.push({
        command,
        response: `Langue changée en ${newLang === 'fr' ? 'français' : 'anglais'}.`
      })
    } else {
      history.value.push({
        command,
        response: "Langue non supportée. Utilisez 'fr' ou 'en'."
      })
    }
    commandHistory.value.push(command)
    currentCommand.value = ''
    historyIndex.value = -1
    return
  }

  // Envoi de la commande au backend
  try {
    const res = await axios.post('/command', {
      command,
      lang: lang.value
    })
    history.value.push({
      command,
      response: res.data.response || 'Erreur : Mon robot s\'est surement perdu, il ne trouve pas le chemin d\'accès. Bip Boop'
    })
  } catch (e) {
    history.value.push({
      command,
      response: 'Erreur : Mon robot n\'arrive pas à se connecter. Bip Boop'
    })
  }
  commandHistory.value.push(command)
  currentCommand.value = ''
  historyIndex.value = -1
}

// Navigation dans l'historique des commandes
const previousCommand = () => {
  if (commandHistory.value.length === 0) return
  if (historyIndex.value < commandHistory.value.length - 1) {
    historyIndex.value++
    currentCommand.value = commandHistory.value[commandHistory.value.length - 1 - historyIndex.value]
  }
}
const nextCommand = () => {
  if (historyIndex.value > 0) {
    historyIndex.value--
    currentCommand.value = commandHistory.value[commandHistory.value.length - 1 - historyIndex.value]
  } else {
    historyIndex.value = -1
    currentCommand.value = ''
  }
}
</script>

<style scoped>
.terminal {
  font-family: 'Fira Mono', monospace;
  padding: 1rem;
  border-radius: 8px;
  width: 100%;
  max-width: 700px;
  margin: 2rem auto;
  min-height: 400px;
  box-shadow: 0 2px 16px #0004;
  position: relative;
}
.terminal.dark {
  background: #1a1a1a;
  color: #eee;
}
.terminal.light {
  background: #f5f5f5;
  color: #222;
  box-shadow: 0 2px 16px #aaa4;
}
.toggle-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: inherit;
  font-size: 1.1em;
  cursor: pointer;
  z-index: 2;
}
.history {
  min-height: 300px;
  margin-bottom: 1rem;
  overflow-y: auto;
}
.command-line {
  display: flex;
  align-items: center;
  color: #8aff80;
  text-align: left;
  margin-bottom: 0.1em;
}
.terminal.dark .command-line {
  color: #8aff80;
}
.terminal.light .command-line {
  color: #228800;
}
.prompt {
  color: #ff5555;
  margin-right: 0.5em;
  font-weight: bold;
  min-width: 180px;
  text-align: left;
}
.terminal.light .prompt {
  color: #d7263d;
}
.cmd {
  color: #8aff80;
}
.terminal.light .cmd {
  color: #228800;
}
.input-line {
  display: flex;
  align-items: center;
  text-align: left;
}
.input {
  background: transparent;
  border: none;
  color: inherit;
  font-family: inherit;
  font-size: 1em;
  outline: none;
  width: 90%;
  margin-left: 0.5em;
}
.response {
  margin-left: 2.5em;
  color: inherit;
  margin-bottom: 0.5em;
  text-align: left;
}
</style>
